const RETRYABLE_STATUSES = [429, 500, 502, 503, 504];

const DEFAULT_MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 300;
const MAX_DELAY_MS = 3000;

export interface RetryTransientRequestOptions {
    maxAttempts?: number;
    baseDelayMs?: number;
    maxDelayMs?: number;
    sleep?: (ms: number) => Promise<void>;
    now?: () => number;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function getRetryDelayMs(
    response: Response | undefined,
    attempt: number,
    { baseDelayMs = BASE_DELAY_MS, maxDelayMs = MAX_DELAY_MS, now = () => Date.now() } = {},
): number {
    const retryAfter = response?.headers?.get('Retry-After');
    if (retryAfter) {
        const seconds = Number(retryAfter);
        if (Number.isFinite(seconds) && seconds >= 0) {
            return Math.min(seconds * 1000, maxDelayMs);
        }
        const date = Date.parse(retryAfter);
        if (!Number.isNaN(date)) {
            return Math.min(Math.max(date - now(), 0), maxDelayMs);
        }
    }
    return Math.min(baseDelayMs * 2 ** attempt, maxDelayMs);
}

export function isRetryableStatus(status: number): boolean {
    return RETRYABLE_STATUSES.includes(status);
}

export async function retryTransientRequest(
    request: () => Promise<Response>,
    options: RetryTransientRequestOptions = {},
): Promise<Response> {
    const { maxAttempts = DEFAULT_MAX_ATTEMPTS, sleep = defaultSleep } = options;
    let lastError: unknown;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const isLastAttempt = attempt === maxAttempts - 1;
        try {
            const response = await request();
            if (!isRetryableStatus(response.status) || isLastAttempt) {
                return response;
            }
            await sleep(getRetryDelayMs(response, attempt, options));
        } catch (error) {
            lastError = error;
            if (isLastAttempt || (error instanceof Error && error.name === 'AbortError')) {
                throw error;
            }
            await sleep(getRetryDelayMs(undefined, attempt, options));
        }
    }

    throw lastError instanceof Error ? lastError : new Error('Request failed');
}
